import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import React, { useEffect, useMemo, useState } from 'react';
import { EmployeeWorkspaceType } from 'src/api/generated';
import { useEmployeeWorkspaces } from 'src/modules/shared/hooks/api/automations/employees/workspaces';
import Loading from 'src/modules/shared/components/atoms/loading';
import { useStatusSelect } from 'src/modules/shared/hooks/api/crm/funnels/status-select';
import { useFunnels } from 'src/modules/shared/hooks/api/crm/funnels/funnels';
import { useFunnelStatuses as useAllFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiTrash } from 'react-icons/pi';
import { FunnelSelector } from 'src/modules/shared/components/molecules/funnel-selector';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import { Table } from 'src/modules/shared/components/molecules/table/table';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import {
  useCreateEmployeeWorkspaceMutation,
  useDeleteEmployeeWorkspaceMutation,
} from 'src/modules/shared/hooks/api/automations/employees/workspaces.mutation';
import { useAlert } from 'src/contexts/alert-context';

interface EmployeeWorkspaceTabProps {
  employeeId: string;
  version: string | number;
}

export const EmployeeWorkspaceTab: React.FC<EmployeeWorkspaceTabProps> = ({
  employeeId,
  version,
}) => {
  const [workspaceType] = useState<EmployeeWorkspaceType>(
    EmployeeWorkspaceType.FUNNEL_STATUS
  );
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [selectedFunnelId, setSelectedFunnelId] = useState<number | undefined>(
    undefined
  );

  const enabled = Boolean(employeeId && version);

  const {
    workspaces,
    loading: isLoading,
    error,
  } = useEmployeeWorkspaces(employeeId, version, enabled);

  const { funnels } = useFunnels({ pageSize: 100 });

  const { statusOptions, loading: statusesLoading } = useStatusSelect({
    selectedFunnelId,
  });

  const { statuses: allStatuses } = useAllFunnelStatuses(
    { pageSize: 500 },
    enabled
  );

  useEffect(() => {
    setWorkspaceId('');
  }, [selectedFunnelId]);

  const { showAlert } = useAlert();

  const { createWorkspace, loading: creating } =
    useCreateEmployeeWorkspaceMutation(
      () => {
        setWorkspaceId('');
        showAlert('Espacio de trabajo agregado', 'success');
      },
      () => {
        showAlert('No se pudo agregar el espacio de trabajo', 'error');
      }
    );
  const { deleteWorkspace, loading: deleting } =
    useDeleteEmployeeWorkspaceMutation(
      () => {
        showAlert('Espacio de trabajo eliminado', 'success');
      },
      () => {
        showAlert('No se pudo eliminar el espacio de trabajo', 'error');
      }
    );

  const canSubmit = useMemo(
    () =>
      enabled &&
      workspaceId.trim().length > 0 &&
      !creating &&
      !statusesLoading &&
      !!selectedFunnelId,
    [enabled, workspaceId, creating, statusesLoading, selectedFunnelId]
  );

  const funnelById = useMemo(() => {
    const map = new Map<number, string>();
    funnels.forEach((f) => map.set(f.id, f.name));
    return map;
  }, [funnels]);

  const statusById = useMemo(() => {
    const map = new Map<number, { name: string; funnelId: number }>();
    allStatuses.forEach((s) =>
      map.set(s.id, { name: s.name, funnelId: s.funnelId })
    );
    return map;
  }, [allStatuses]);

  interface WorkspaceRow {
    rowId: string;
    funnel: string;
    status: string;
    workspaceId: string;
  }

  const columns = [
    { header: 'Embudo', accessor: 'funnel', className: '' },
    { header: 'Estado', accessor: 'status', className: '' },
    {
      header: 'Acciones',
      accessor: 'workspaceId',
      className: 'text-right',
      render: (_: unknown, row: WorkspaceRow) => (
        <Button
          variant="danger"
          appearance="soft"
          size="sm"
          type="button"
          onClick={() =>
            deleteWorkspace({
              workspaceType,
              employeeId,
              version,
              workspaceId: row.workspaceId,
            })
          }
          disabled={deleting}
          className="flex items-center gap-1"
        >
          <PiTrash className="w-4 h-4" />
          <Text size="small">Eliminar</Text>
        </Button>
      ),
    },
  ];

  const tableData: WorkspaceRow[] = useMemo(() => {
    return workspaces.map((w) => {
      const statusInfo = statusById.get(Number(w.workspaceId));
      const funnelName = statusInfo
        ? funnelById.get(statusInfo.funnelId)
        : undefined;
      const statusName =
        nonEmptyString(statusInfo?.name) ?? String(w.workspaceId);
      return {
        rowId: `${w.workspaceType}:${w.workspaceId}`,
        funnel: nonEmptyString(funnelName) ?? '—',
        status: statusName,
        workspaceId: w.workspaceId,
      };
    });
  }, [workspaces, statusById, funnelById]);

  return (
    <div className="space-y-6 mt-6">
      <Panel variant="neutral" appearance="outline">
        <PanelHeader size="sm" fixed>
          <Text size="h6" weight="medium">
            {/* TODO: check when implementing i18n */}
            Embudos
          </Text>
        </PanelHeader>

        <div className="p-4 space-y-4">
          {!enabled && (
            <Text size="small" className="text-gray-500">
              {/* TODO: check when implementing i18n */}
              Guarda el agente para poder gestionar sus espacios de trabajo.
            </Text>
          )}

          <div className="flex flex-col md:flex-row gap-3 md:items-end">
            <div className="flex-1">
              <FunnelSelector
                funnels={funnels}
                selectedFunnelId={selectedFunnelId}
                onFunnelSelect={(id) => setSelectedFunnelId(id)}
                size="sm"
                className="w-full"
              />
            </div>

            <div className="flex-1">
              <LabelSelect
                // TODO: check when implementing i18n
                label="Estado"
                labelPosition="top"
                size="sm"
                options={[
                  {
                    content: !selectedFunnelId
                      ? // TODO: check when implementing i18n
                        'Selecciona un embudo primero'
                      : 'Selecciona un estado',
                    value: '',
                  },
                  ...statusOptions.map((s) => ({
                    content: s.name,
                    value: String(s.id),
                  })),
                ]}
                selectProps={{
                  value: workspaceId,
                  onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
                    setWorkspaceId(e.target.value),
                  loading: statusesLoading,
                  disabled:
                    !enabled ||
                    creating ||
                    statusesLoading ||
                    !selectedFunnelId,
                }}
              />
            </div>

            <div>
              <Button
                type="button"
                variant="primary"
                appearance="filled"
                size="sm"
                onClick={() =>
                  createWorkspace({
                    workspaceType,
                    employeeId,
                    version,
                    workspaceId,
                  })
                }
                disabled={!canSubmit}
                className="px-4"
              >
                {/* TODO: check when implementing i18n */}
                Agregar
              </Button>
            </div>
          </div>

          <div className="mt-4">
            {isLoading ? (
              <Loading size="sm" />
            ) : error ? (
              <Text size="small" className="text-danger-700">
                {/* TODO: check when implementing i18n */}
                Error al cargar
              </Text>
            ) : workspaces.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                <Text size="paragraph">
                  {/* TODO: check when implementing i18n */}
                  No hay estados de embudo vinculados.
                </Text>
                <Text size="small" className="mt-2">
                  {/* TODO: check when implementing i18n */}
                  Agrega estados del embudo para vincular el agente con su
                  espacio de trabajo.
                </Text>
              </div>
            ) : (
              <Table
                columns={columns}
                data={tableData}
                keyExtractor={(row: WorkspaceRow) => row.rowId}
                variant="neutral"
              />
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
};
